import { z } from 'zod';
import { commonContractTypeSchema, rfpRequirementSchema, } from './contracts.js';
import { dateStringSchema } from './dateString.js';
export const rfpClarifyingQaSchema = z.object({
    question: z.string(),
    context: z.string().optional(),
    answer: z.string(),
});
export const rfpSourceKindSchema = z.enum(['contract', 'rfp']);
export const rfpCreationMethodSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('ai'),
        brief: z.string(),
        clarifyingQas: z.array(rfpClarifyingQaSchema),
    }),
    z.object({
        kind: z.literal('upload'),
        sourceDocumentId: z.string(),
        adaptationInstructions: z.string(),
        clarifyingQas: z.array(rfpClarifyingQaSchema),
        sourceKind: rfpSourceKindSchema.optional(),
    }),
    z.object({
        kind: z.literal('manual'),
        workDescription: z.string().min(1),
        contactName: z.string().min(1),
        contactEmail: z.string().email(),
        contactPhone: z.string().optional(),
        requirements: z.array(rfpRequirementSchema),
        templateName: z.string().optional(),
    }),
]);
export const rfpLastEditedBySchema = z.enum(['ai', 'user']);
export const rfpDraftSectionSchema = z.object({
    id: z.string(),
    title: z.string(),
    content: z.string(),
    lastEditedBy: rfpLastEditedBySchema,
    updatedAt: dateStringSchema,
});
export const rfpSectionSchema = z.object({
    id: z.string(),
    title: z.string(),
    content: z.string(),
});
export const rfpCriterionSourceSchema = z.enum(['template', 'manual', 'extracted']);
export const rfpCriterionSchema = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    source: rfpCriterionSourceSchema,
});
const rfpIdentityShape = {
    id: z.string(),
    displayId: z.string(),
    title: z.string(),
    proposalDeadline: dateStringSchema,
    ownershipId: z.string(),
    portfolioId: z.string(),
    propertyId: z.string(),
    contractType: commonContractTypeSchema,
    creationMethod: rfpCreationMethodSchema,
    createdAt: dateStringSchema,
    createdByUserId: z.string(),
};
export const publicationEmailDeliverySchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('disabled') }),
    z.object({ kind: z.literal('sent'), sentAt: dateStringSchema }),
    z.object({
        kind: z.literal('failed'),
        failedAt: dateStringSchema,
        reason: z.string(),
    }),
]);
export const rfpPublicationSchema = z.object({
    publishedAt: dateStringSchema,
    publishedByUserId: z.string(),
    publishedSections: z.array(rfpSectionSchema),
    lockedCriteria: z.array(rfpCriterionSchema),
    recipientEmails: z.array(z.string()),
    publicationMessage: z.string().optional(),
    publicationDocumentId: z.string().optional(),
    emailDelivery: publicationEmailDeliverySchema,
});
export const rfpSchema = z.discriminatedUnion('kind', [
    z.object({
        ...rfpIdentityShape,
        kind: z.literal('draft'),
        draftSections: z.array(rfpDraftSectionSchema),
        availableCriteria: z.array(rfpCriterionSchema),
        extractedCriteriaCandidates: z.array(rfpCriterionSchema).optional(),
    }),
    z.object({
        ...rfpIdentityShape,
        kind: z.literal('collecting'),
        publication: rfpPublicationSchema,
    }),
    z.object({
        ...rfpIdentityShape,
        kind: z.literal('in_review'),
        publication: rfpPublicationSchema,
        reviewMovedAt: dateStringSchema,
    }),
    z.object({
        ...rfpIdentityShape,
        kind: z.literal('closed'),
        publication: rfpPublicationSchema,
        closedAt: dateStringSchema,
        closeReason: z.enum(['approved', 'rejected', 'executed']),
    }),
]);
export const rfpCollectionStatusSchema = z.enum(['draft', 'collecting', 'in_review', 'closed']);
export const proposalSchema = z.object({
    id: z.string(),
    displayId: z.string(),
    rfpId: z.string(),
    vendorId: z.string(),
    createdAt: dateStringSchema,
    latestVersionId: z.string(),
});
export const proposalVersionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('uploaded'),
        id: z.string(),
        displayId: z.string(),
        proposalId: z.string(),
        versionNumber: z.number().int().positive(),
        documentId: z.string(),
        uploadedAt: dateStringSchema,
        uploadedByUserId: z.string(),
    }),
    z.object({
        kind: z.literal('superseded'),
        id: z.string(),
        displayId: z.string(),
        proposalId: z.string(),
        versionNumber: z.number().int().positive(),
        documentId: z.string(),
        uploadedAt: dateStringSchema,
        uploadedByUserId: z.string(),
        supersededAt: dateStringSchema,
        supersededByVersionId: z.string(),
    }),
]);
export const proposalVersionStatusSchema = z.enum(['uploaded', 'superseded']);
export const proposalSummarySchema = z.object({
    headline: z.string(),
    scopeOfWork: z.string(),
    vendorFraming: z.string().optional(),
    flaggedConcerns: z.array(z.string()),
});
export const proposalCommercialTermsSchema = z.object({
    totalAmount: z.number().optional(),
    paymentTerms: z.string().optional(),
    termLength: z.string().optional(),
    renewalTerms: z.string().optional(),
    notes: z.string().optional(),
});
export const proposalCriterionAssessmentSchema = z.object({
    criterionId: z.string(),
    score: z.number().optional(),
    finding: z.string(),
});
export const proposalRiskScoreSchema = z.object({
    dimension: z.string(),
    score: z.number(),
    rationale: z.string(),
});
export const proposalAnalysisRunSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('pending'),
        id: z.string(),
        proposalVersionId: z.string(),
        startedAt: dateStringSchema,
        startedByUserId: z.string(),
    }),
    z.object({
        kind: z.literal('completed'),
        id: z.string(),
        proposalVersionId: z.string(),
        startedAt: dateStringSchema,
        startedByUserId: z.string(),
        completedAt: dateStringSchema,
        summary: proposalSummarySchema,
        commercialTerms: proposalCommercialTermsSchema,
        criterionAssessments: z.array(proposalCriterionAssessmentSchema),
        riskScores: z.array(proposalRiskScoreSchema),
    }),
    z.object({
        kind: z.literal('failed'),
        id: z.string(),
        proposalVersionId: z.string(),
        startedAt: dateStringSchema,
        startedByUserId: z.string(),
        failedAt: dateStringSchema,
        failureReason: z.string(),
    }),
]);
export const proposalAnalysisRunStatusSchema = z.enum(['pending', 'completed', 'failed']);
export const comparisonSessionSchema = z.object({
    id: z.string(),
    displayId: z.string(),
    rfpId: z.string(),
    createdAt: dateStringSchema,
    createdByUserId: z.string(),
    proposalVersionIds: z.array(z.string()),
});
export const rfpProposalReviewSnapshotSchema = z.object({
    selectedVendorName: z.string(),
    summaryText: z.string().optional(),
    totalValue: z.number().optional(),
});
export const signedContractAttachmentSchema = z.object({
    documentId: z.string(),
    uploadedAt: dateStringSchema,
    uploadedByUserId: z.string(),
});
const reviewIdentityShape = {
    id: z.string(),
    displayId: z.string(),
    rfpId: z.string(),
    proposalId: z.string(),
    proposalVersionId: z.string(),
    comparisonSessionId: z.string(),
    snapshot: rfpProposalReviewSnapshotSchema,
    createdAt: dateStringSchema,
    createdByUserId: z.string(),
};
export const rfpProposalReviewSchema = z.discriminatedUnion('kind', [
    z.object({ ...reviewIdentityShape, kind: z.literal('pending_review') }),
    z.object({
        ...reviewIdentityShape,
        kind: z.literal('under_review'),
        reviewStartedAt: dateStringSchema,
        reviewerUserId: z.string(),
        reviewNotes: z.string().optional(),
    }),
    z.object({
        ...reviewIdentityShape,
        kind: z.literal('approved'),
        approvedAt: dateStringSchema,
        reviewerUserId: z.string(),
        reviewNotes: z.string().optional(),
        signedContract: signedContractAttachmentSchema.optional(),
    }),
    z.object({
        ...reviewIdentityShape,
        kind: z.literal('rejected'),
        rejectedAt: dateStringSchema,
        reviewerUserId: z.string(),
        reviewNotes: z.string().optional(),
    }),
    z.object({
        ...reviewIdentityShape,
        kind: z.literal('executed'),
        approvedAt: dateStringSchema,
        executedAt: dateStringSchema,
        reviewerUserId: z.string(),
        reviewNotes: z.string().optional(),
        signedContract: signedContractAttachmentSchema,
        executedContractId: z.string(),
    }),
]);
export const rfpProposalReviewStatusSchema = z.enum([
    'pending_review',
    'under_review',
    'approved',
    'rejected',
    'executed',
]);
export function llmOptional(schema) {
    return schema.nullable().optional();
}
export const rfpMatchVerdictSchema = z.enum(['match', 'unclear', 'mismatch']);
export const rfpMatchSchema = z.object({
    verdict: rfpMatchVerdictSchema,
    matchReasoning: z.string(),
});
//# sourceMappingURL=rfp.js.map